<template>
  <div class="flex">
    <v-slide-x-transition>
      <div v-if="showFormWarning" class="flex mr-2 items-center">
        <v-tooltip location="top" color="error">
          <template #activator="{ props }">
            <v-icon
              v-bind="props"
              size="28"
              color="error"
            >
              mdi-alert-circle
            </v-icon>
          </template>
          <span>Other users are editing this form, your changes may be lost</span>
        </v-tooltip>
      </div>
    </v-slide-x-transition>
    <div class="avatar-group flex -space-x-1 items-center cursor-default">
      <template v-for="(user, index) of currentLocationAvatars" :key="index">
        <v-tooltip location="top" transition="fade-transition" :model-value="user.persistentStatus">
          <template #activator="{ props }">
            <Avatar
              v-bind="mergeProps(props, user)"
              :size="32"
              @mouseenter="hover(user)"
            />
          </template>
          <span>{{ getUserStatus(user, user.status) }}</span>
        </v-tooltip>
      </template>
      <v-tooltip location="top" transition="fade-transition">
        <template #activator="{ props }">
          <div
            v-if="currentLocationAvatars.length > 0"
            v-bind="props"
            class="flex items-center justify-center w-8 h-8 text-xl font-medium bg-white/30 rounded-full cursor-pointer hover:bg-white/50 transition-all"
            @click="showActiveUsers"
          >
            +
          </div>
        </template>
        <span> {{ currentLocationAvatars.length }} {{ currentLocationAvatars.length === 1 ? 'person' : 'people' }} viewing
        </span>
      </v-tooltip>
    </div>
    <v-dialog v-model="dialog" max-width="400px">
      <v-card class="py-2 px-2">
        <v-card-title>Active Users in this Policy</v-card-title>
        <v-card-subtitle class="!whitespace-normal mb-4">
          {{ currentLocationAvatars.length }} {{ currentLocationAvatars.length === 1 ? 'person is' : 'people are' }} currently viewing or editing.
        </v-card-subtitle>
        <v-card-text>
          <div v-for="(user, index) of currentLocationAvatars" :key="index" class="flex items-center gap-3 mb-2">
            <Avatar v-bind="user" :size="32" />
            <span>{{ user.first }} {{ user.last }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            variant="flat"
            prepend-icon="mdi-content-copy"
            @click="copyLink"
          >
            Copy Link
          </v-btn>
          <v-spacer />
          <v-btn variant="outlined" @click="dialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, mergeProps, ref, watch } from 'vue'

import Avatar from '@/components/common/Avatar/Avatar.vue'
import { useClipboard } from '@/composables/clipboard'
import { isSameGroupLocation, useWebSocketStore } from '@/stores/webSocket'

import type { GroupMessageHandler } from '@/stores/webSocket'
import type { ConnectionDetail, GroupDetail, WebSocketMessage_GroupUpdate } from '@policyfly/protobuf/patrick'
import type { PropType } from 'vue'

const PERSIST_AVATAR_STATUS_UPDATE = 10_000

export default defineComponent({
  name: 'LocationAvatars',

  components: {
    Avatar,
  },

  props: {
    location: { type: Object as PropType<GroupDetail['location'] | undefined>, default: undefined },
  },

  setup (props) {
    const webSocketStore = useWebSocketStore()
    const currentLocation = computed(() => {
      return webSocketStore.groups.find(({ location }) => isSameGroupLocation(location, props.location))
    })
    const { copy } = useClipboard()

    const currentLocationAvatars = computed(() => {
      const formGroup = webSocketStore.groups.find((g) => g.location.oneofKind === 'form')
      return (currentLocation.value?.users || []).map((user, seed) => {
        const inSameForm = user.uuid !== webSocketStore.uuid && formGroup?.users.some((u) => u.uuid === user.uuid && !u.ghost)
        const status = (latestActivityMessage.value?.connectionDetail?.uuid === user.uuid && latestActivityMessage.value.text)
          ? latestActivityMessage.value.text
          : user.ghost
            ? 'has left the application'
            : inSameForm
              ? 'is editing this form'
              : 'is viewing the application'
        return {
          ...user,
          first: user.userDetail?.firstName,
          last: user.userDetail?.lastName,
          status,
          persistentStatus: status === latestActivityMessage.value?.text,
          seed,
          border: `${inSameForm ? 'error md' : 'white sm'} solid opacity-100`,
          class: user.ghost ? 'grayscale brightness-75' : undefined,
        }
      })
    })

    const hoveredUser = ref<ConnectionDetail | null>(null)
    function hover (user: ConnectionDetail): void {
      hoveredUser.value = user
    }
    const latestActivityMessage = ref<WebSocketMessage_GroupUpdate | null>(null)
    const latestActivityTimeout = ref<number | null>(null)
    const getUserStatus = (user: ConnectionDetail, status: string): string => {
      return `${user.userDetail?.firstName} ${user.userDetail?.lastName} ${status}`
    }

    const dialog = ref(false)
    function showActiveUsers (): void {
      dialog.value = true
    }

    function copyLink (): void {
      copy(window.location.href)
    }

    const handler: GroupMessageHandler<'groupUpdate'> = (update) => {
      const { groupDetail, connectionDetail, text } = update
      switch (groupDetail?.location.oneofKind) {
        case 'application':
          // display user status update text
          if (connectionDetail && text) {
            latestActivityMessage.value = update
            if (latestActivityTimeout.value) window.clearTimeout(latestActivityTimeout.value)
            latestActivityTimeout.value = window.setTimeout(
              async () => {
                latestActivityMessage.value = null
                latestActivityTimeout.value = null
              },
              PERSIST_AVATAR_STATUS_UPDATE,
            )
          }
          break
      }
    }
    watch(() => props.location, (location, _prev, onCleanup) => {
      if (!location) return
      const unsubscribe = webSocketStore.subscribe('groupUpdate', location, handler)
      onCleanup(unsubscribe)
    }, { immediate: true })

    const showFormWarning = computed(() => {
      return webSocketStore.groups.some(({ location, users }) => {
        return location.oneofKind === 'form' && users.filter((u) => !u.ghost).length > 1
      })
    })

    return {
      currentLocationAvatars,
      dialog,
      hoveredUser,
      hover,
      showActiveUsers,
      copyLink,
      mergeProps,
      getUserStatus,

      showFormWarning,
    }
  },
})
</script>
